/**
 * Cola de acciones pendientes en IndexedDB.
 *
 * En planta la red se cae: wifi con zonas muertas, el switch que se reinicia,
 * el servidor que se actualiza. El operario no puede dejar de fichar por eso.
 * Cada acción se guarda con la hora real en que se pulsó el botón y con un
 * identificador propio, así el servidor la acepta una sola vez cuando vuelva
 * la conexión, sin importar cuántas veces se reintente.
 */
export type QueuedKind = 'clock' | 'startPhase' | 'pausePhase' | 'finishPhase' | 'closeOrder';

export interface QueuedAction {
  id: string;
  kind: QueuedKind;
  payload: unknown;
  createdAt: string;
  description: string;
}

const DB_NAME = 'ecinsa-terminal';
const STORE = 'outbox';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'id' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function tx<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb();
  return new Promise<T>((resolve, reject) => {
    const transaction = db.transaction(STORE, mode);
    const request = fn(transaction.objectStore(STORE));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    transaction.oncomplete = () => db.close();
  });
}

export async function enqueue(action: QueuedAction): Promise<void> {
  await tx('readwrite', (store) => store.put(action));
}

export async function pending(): Promise<QueuedAction[]> {
  const all = await tx<QueuedAction[]>('readonly', (store) => store.getAll() as IDBRequest<QueuedAction[]>);
  return all.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export async function remove(id: string): Promise<void> {
  await tx('readwrite', (store) => store.delete(id) as unknown as IDBRequest<undefined>);
}

export async function count(): Promise<number> {
  return tx<number>('readonly', (store) => store.count());
}

export async function clear(): Promise<void> {
  await tx('readwrite', (store) => store.clear() as unknown as IDBRequest<undefined>);
}
